import React, { FunctionComponent, useState } from "react"
import {
    CircularProgress,
    FormControl,
    TextField,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Grid,
    Switch,
    Tooltip
} from "@material-ui/core"
import { BatchItem } from "./BatchItem"
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown"
import styles from "./BatchRow.module.scss"
import { BatchItemCard } from "./BatchItemCard"
import { ErrorOutlineOutlined, Subject, Audiotrack } from "@material-ui/icons"
import { useBatchPageContext } from "./useBatchPageContext"
import { updateItem } from "./reducer"
import { useStepControlsContext } from "../../features/StepControls/useStepControlsContext"
import {
    getTooltipErrorMsg,
    formHasValidationOrOperationErrors
} from "./validation/ValidationHelpers"
import { useBatchItemValidator } from "./validation/useBatchItemValidator"

interface BatchRowProps {
    rowItem: BatchItem
}

export const BatchRow: FunctionComponent<BatchRowProps> = props => {
    const { rowItem } = props
    const { dispatch } = useBatchPageContext()
    const { state: stepper } = useStepControlsContext()
    const [open, setOpen] = useState<boolean>(false)

    const formik = useBatchItemValidator(rowItem)
    const { values, errors, handleChange, handleBlur, isValidating } = formik

    // items are editable if they haven't been approved
    const editable = () => {
        if (rowItem.target)
            return !rowItem.downloadableSourceAudio || !rowItem.downloadableTargetAudio
        return !rowItem.downloadableSourceAudio
    }

    const lockDuringApproveAudio = () => {
        return stepper.activeStep === 3 && !rowItem.sourceAudio && !rowItem.targetAudio
    }

    const locked = () => {
        return !editable() || lockDuringApproveAudio()
    }

    const toggleInclude = () => {
        if (editable()) {
            dispatch(updateItem(rowItem.index, { include: !rowItem.include }))
        }
    }

    let blur = !rowItem.include ? styles.BatchRow__blur : ""
    let grey = !rowItem.include ? styles.BatchRow__grey : styles.BatchRow

    if (locked()) {
        blur = styles.BatchRow__blur
        grey = styles.BatchRow__grey
    }

    const errorsPresent = formHasValidationOrOperationErrors(errors)

    return (
        <>
            <TableRow className={grey}>
                <TableCell className={styles.BatchRow__cell}>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justify="flex-start"
                        spacing={1}
                    >
                        <Grid item className={blur}>
                            <IconButton
                                size="small"
                                onClick={() => setOpen(!open)}
                                data-testid="openButton"
                                className="openButton"
                            >
                                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </Grid>

                        <Grid item className={styles.BatchRow__rowItem}>
                            <Grid container spacing={2} direction={"row"}>
                                <Grid item data-testid={rowItem.name}>
                                    <FormControl id={`promptName${rowItem.index}`}>
                                        <TextField
                                            name="name"
                                            required
                                            value={values.name}
                                            label={"Prompt Name"}
                                            onChange={handleChange}
                                            data-testid="itemName"
                                            onBlur={handleBlur}
                                            error={!!errors.name && rowItem.include}
                                            helperText={rowItem.include ? errors.name : undefined}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    {isValidating && (
                                        <CircularProgress
                                            color={"secondary"}
                                            size={20}
                                            className={styles.BatchRow__spinner}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>

                        {rowItem.include && errorsPresent && (
                            <Grid item className={blur}>
                                <Tooltip title={getTooltipErrorMsg(errors)} placement={"right"}>
                                    <ErrorOutlineOutlined
                                        id="errorIcon"
                                        data-testid="errorIcon"
                                        className={styles.BatchRow__errorIcon}
                                        color="error"
                                    />
                                </Tooltip>
                            </Grid>
                        )}

                        <Grid item className={styles.BatchRow__translateIcon}>
                            {rowItem.target && (
                                <Subject color="secondary" data-testid="translateIcon" />
                            )}
                        </Grid>
                        <Grid item className={styles.BatchRow__audioIcon}>
                            {(rowItem.sourceAudio || rowItem.targetAudio) && (
                                <Audiotrack color="secondary" data-testid="audioIcon" />
                            )}
                        </Grid>
                        <Grid item className={styles.BatchRow__include}>
                            <Switch
                                data-testid="include-switch"
                                onChange={toggleInclude}
                                checked={locked() ? false : rowItem.include}
                            />
                        </Grid>
                    </Grid>
                </TableCell>
            </TableRow>
            <TableRow className={grey}>
                <TableCell>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <BatchItemCard item={rowItem} {...formik} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}
